import { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Check, Copy, Minus, Plus, RefreshCw, AlertTriangle, CircleCheck, Info, CircleX, Clock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { STATUS, copyText } from "@/lib/tickets";
import { cn } from "@/lib/utils";

export const EASE = [0.2, 0.8, 0.2, 1];

/** Status pill with a leading dot. `status` = reserved|confirmed|waitlist|expired|cancelled|warning|muted */
export const TicketStatus = ({ status, label, className, ...props }) => {
  const s = STATUS[status];
  const cls = { reserved: "badge-reserved", payment_check: "badge-warning", confirmed: "badge-confirmed", waitlist: "badge-waitlist", expired: "badge-expired", cancelled: "badge-cancelled", warning: "badge-warning" }[status] || "badge-muted";
  return <span className={cn("badge-status", cls, className)} data-status={status} {...props}>{label || s?.label || status}</span>;
};

const STEPS = {
  vipps_manual: ["Reservert", "Betaling registrert", "Billett klar"],
  door: ["Reservert", "Billett klar", "Betal i døra"],
  free: ["Påmeld", "Billett klar"],
};

/** Hairline step track that stays anchored while the state changes. */
export const TicketProgress = ({ mode = "vipps_manual", status, className }) => {
  const steps = [...(STEPS[mode] || STEPS.vipps_manual)];
  if (mode === "vipps_manual" && status !== "confirmed") steps[1] = status === "payment_check" ? "Betalingskontroll" : "Ventar på registrering";
  const closed = status === "expired" || status === "cancelled";
  let current = 0; // index of the step that is "in progress"
  if (status === "waitlist") current = -1;
  else if (status === "confirmed") current = mode === "door" ? steps.length - 1 : steps.length;
  else if (status === "reserved" || status === "payment_check") current = 1;
  const label = status === "waitlist" ? "Venteliste – før steg 1" : closed ? STATUS[status].label : `Steg ${Math.min(current + (status === "confirmed" && mode !== "door" ? 0 : 1), steps.length)} av ${steps.length}`;
  return (
    <ol className={cn("flex items-start", className)} aria-label={`Framdrift: ${label}`} data-testid="ticket-progress-indicator" data-current={current}>
      {steps.map((s, i) => {
        const done = !closed && i < current;
        const active = !closed && i === current;
        return (
          <li key={s} className={cn("flex min-w-0 items-start", i < steps.length - 1 && "flex-1")}>
            <div className="flex min-w-0 flex-col items-start">
              <span className={cn("relative mt-[3px] block h-2.5 w-2.5 rounded-full border", done ? "border-foreground bg-foreground" : active ? "border-sol bg-sol" : closed ? "border-border bg-secondary" : "border-input bg-background")} aria-hidden="true">
                {active && <span className="absolute -inset-1.5 rounded-full border border-sol/50" />}
              </span>
              <span className={cn("t-mono-sm mt-2 pr-3 sm:whitespace-nowrap", done || active ? "text-foreground" : "text-muted-foreground", closed && "line-through decoration-border")}>{s}</span>
            </div>
            {i < steps.length - 1 && <span className={cn("mx-3 mt-[7px] h-px flex-1", done ? "bg-foreground" : "bg-border")} aria-hidden="true" />}
          </li>
        );
      })}
    </ol>
  );
};

/** Big copyable value row (Vipps number, amount, code). */
export const CopyField = ({ label, value, hint, testId, size = "lg" }) => {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => { if (await copyText(String(value))) { setDone(true); toast.success(`${label} kopiert`); setTimeout(() => setDone(false), 1600); } else toast.error("Kunne ikkje kopiere – marker teksten manuelt"); }}
      className="group -mx-3 flex w-[calc(100%+24px)] items-center justify-between gap-4 rounded-[12px] px-3 py-3.5 text-left transition-colors hover:bg-card focus-visible:bg-card sm:py-4"
      data-testid={testId}
      aria-label={`Kopier ${label}: ${value}`}
    >
      <span className="min-w-0">
        <span className="t-mono block text-muted-foreground">{label}</span>
        <span className={cn("mt-1 block truncate font-display font-semibold tabular tracking-[-0.02em]", size === "lg" ? "text-[clamp(26px,5vw,38px)] leading-none" : "text-[22px] leading-tight")}>{value}</span>
        {hint && <span className="mt-1 block text-[13px] text-muted-foreground">{hint}</span>}
      </span>
      <span className={cn("flex h-11 w-11 shrink-0 items-center justify-center rounded-full border transition-colors", done ? "border-transparent bg-status-confirmed text-paper" : "border-border bg-card text-muted-foreground group-hover:border-foreground group-hover:text-foreground")}>
        {done ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      </span>
    </button>
  );
};

const BANNER_ICON = { ok: CircleCheck, warn: AlertTriangle, bad: CircleX, muted: Info, pending: Clock };

/** Calm verdict/notice surface. variant = ok|warn|bad|muted|pending */
export const ResultBanner = ({ variant = "muted", icon, title, text, action, className, children, testId, live = true }) => {
  const Icon = icon || BANNER_ICON[variant] || Info;
  const cls = { ok: "verdict-ok", warn: "verdict-warn", bad: "verdict-bad", muted: "verdict-muted", pending: "verdict-warn" }[variant];
  const iconCls = { ok: "text-status-confirmed", warn: "text-status-warning", bad: "text-status-cancelled", muted: "text-muted-foreground", pending: "text-foreground" }[variant];
  return (
    <div role={live ? "status" : undefined} aria-live={live ? "polite" : undefined} className={cn("rounded-[20px] border p-5 sm:p-6", cls, className)} data-testid={testId} data-variant={variant}>
      <div className="flex items-start gap-4">
        <Icon className={cn("mt-0.5 h-5 w-5 shrink-0", iconCls)} aria-hidden="true" />
        <div className="min-w-0 flex-1">
          {title && <p className="font-display text-[20px] font-semibold leading-tight tracking-[-0.01em]">{title}</p>}
          {text && <p className="mt-1.5 text-[15px] leading-relaxed text-foreground/80">{text}</p>}
          {children}
          {action && <div className="mt-4 flex flex-wrap gap-2">{action}</div>}
        </div>
      </div>
    </div>
  );
};

/** Thumb-friendly stepper. The plus button is filled while the value is 0 to invite the first tap. */
export const TicketStepper = ({ value, onChange, min = 0, max = Infinity, id, disabled = false, name }) => {
  const reduce = useReducedMotion();
  const canDec = !disabled && value > min;
  const canInc = !disabled && value < max;
  return (
    <div className="flex items-center gap-1.5" role="group" aria-label={name ? `Tal ${name}` : "Tal billettar"} data-testid={`ticket-type-${id}-stepper`}>
      <Button type="button" variant="outline" size="icon" className="h-11 w-11 rounded-full border-foreground/15 bg-card hover:border-foreground active:scale-95 disabled:opacity-30" onClick={() => onChange(value - 1)} disabled={!canDec} aria-label="Færre" data-testid={`ticket-type-${id}-minus`}><Minus className="h-4 w-4" /></Button>
      <span className="relative flex h-11 w-9 items-center justify-center overflow-hidden text-[18px] font-semibold tabular" aria-live="polite" data-testid={`ticket-type-${id}-qty`}>
        <AnimatePresence mode="popLayout" initial={false}>
          <motion.span key={value} initial={reduce ? false : { y: 10, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={reduce ? { opacity: 0 } : { y: -10, opacity: 0 }} transition={{ duration: 0.18, ease: EASE }}>{value}</motion.span>
        </AnimatePresence>
      </span>
      <Button type="button" variant={value === 0 ? "default" : "outline"} size="icon" className={cn("h-11 w-11 rounded-full active:scale-95 disabled:opacity-30", value !== 0 && "border-foreground/15 bg-card hover:border-foreground")} onClick={() => onChange(value + 1)} disabled={!canInc} aria-label="Fleire" data-testid={`ticket-type-${id}-plus`}><Plus className="h-4 w-4" /></Button>
    </div>
  );
};

/** Thin capacity bar: how much of the room is already taken. */
export const CapacityMeter = ({ available, capacity, fewLeft = false, className }) => {
  if (!capacity) return null;
  const taken = Math.min(100, Math.max(0, Math.round(((capacity - (available ?? 0)) / capacity) * 100)));
  return (
    <div className={cn("space-y-1.5", className)} data-testid="ticket-capacity-meter" data-pct={taken}>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-secondary" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={taken} aria-label={`${taken} % av plassane er tekne`}>
        <motion.div className={cn("h-full rounded-full", fewLeft ? "bg-sol" : available === 0 ? "bg-muted-foreground" : "bg-foreground")} initial={{ width: 0 }} animate={{ width: `${taken}%` }} transition={{ duration: 0.6, ease: EASE }} />
      </div>
      {taken > 0 && <p className={cn("text-right text-[12px] tabular", fewLeft ? "font-medium text-sol" : "text-muted-foreground")} data-testid="ticket-capacity-pct">{available === 0 ? "Fullt" : `${taken} % fylt`}</p>}
    </div>
  );
};

/** "This page updates itself" line with a manual refresh. */
export const LiveNote = ({ text, onRefresh, refreshing = false, className }) => (
  <div className={cn("flex flex-wrap items-center gap-x-3 gap-y-2 text-[13px] text-muted-foreground", className)} data-testid="ticket-live-note">
    <span className="live-dot" aria-hidden="true" />
    <span>{text}</span>
    {onRefresh && <Button type="button" variant="ghost" size="sm" className="h-8 px-2 text-[13px]" onClick={onRefresh} disabled={refreshing} data-testid="ticket-refresh-button"><RefreshCw className={cn("h-3.5 w-3.5", refreshing && "animate-spin")} /> Oppdater</Button>}
  </div>
);

/** Live "time left" that re-renders every 30 s. Returns {ms, text, urgent}. */
export const useCountdown = (iso) => {
  const [, tick] = useState(0);
  useEffect(() => { const t = setInterval(() => tick((n) => n + 1), 30000); return () => clearInterval(t); }, []);
  if (!iso) return { ms: 0, text: "", urgent: false };
  const ms = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(ms) || ms <= 0) return { ms: 0, text: "fristen er ute", urgent: true };
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const d = Math.floor(h / 24);
  const text = d >= 1 ? `${d} d ${h % 24} t` : h >= 1 ? `${h} t ${m} min` : `${m} min`;
  return { ms, text, urgent: ms < 6 * 3600000 };
};
